/**
 * Agent conversation stream helper (epic-6 §2c / AC-13).
 *
 * Subscribes the socket.io room `agent:conversations:<id>` on the gateway and
 * forwards `agent:stream` parts to a `ThinkingBuffer` that accumulates text +
 * reasoning deltas AND tool-call/tool-result activity keyed by step index
 * (idempotent against replay: the worker + gateway bridge may re-send the last
 * persisted parts when a client joins mid-run).
 *
 * Usage:
 *   const buffer = new ThinkingBuffer();
 *   const stop = subscribeAgentStream(conversationId, {
 *     onPart: (part) => buffer.addPart(part),
 *   });
 *   stop();
 */

import { wsClient } from './ws';
import type { AgentStreamPart, AgentStreamEnvelope } from '@/types';

/** Socket.io room name for a conversation's live stream. */
export function agentConversationRoom(conversationId: string): string {
  return `agent:conversations:${conversationId}`;
}

/**
 * Normalize an incoming `agent:stream` payload into a `{ part, at }` envelope.
 * Tolerates all shapes the bridge may emit:
 *  - `{ t: 'part', part, at }`  (worker Redis envelope, replayed verbatim)
 *  - `{ conversationId, part, at }` (gateway bridge emit)
 *  - the raw part object itself.
 */
export function parseAgentStreamPayload(payload: unknown): AgentStreamEnvelope {
  if (!payload || typeof payload !== 'object') return { part: undefined };
  const candidate = payload as Record<string, unknown>;

  if (candidate.t === 'part' && candidate.part) {
    return {
      t: 'part',
      part: candidate.part as AgentStreamPart,
      at: typeof candidate.at === 'string' ? candidate.at : undefined,
    };
  }
  if (candidate.part && typeof candidate.part === 'object') {
    return {
      part: candidate.part as AgentStreamPart,
      conversationId:
        typeof candidate.conversationId === 'string' ? candidate.conversationId : undefined,
      at: typeof candidate.at === 'string' ? candidate.at : undefined,
    };
  }
  // Raw part shape (e.g. { type: 'reasoning-delta', text: '...' })
  return { part: (candidate as unknown as AgentStreamPart) };
}

/** A streaming text-delta part's delta may live in `delta` (AI SDK v4) or `text` (v7). */
export function deltaTextOf(part: AgentStreamPart): string | undefined {
  const p = part as Partial<{ text?: unknown; delta?: unknown }>;
  if (typeof p.text === 'string' && p.text.length > 0) return p.text;
  if (typeof p.delta === 'string') return p.delta;
  return undefined;
}

/**
 * Captured tool activity from `tool-call` / `tool-result` stream parts.
 * A `tool-call` becomes a `toolParts` entry without `output`; the matching
 * `tool-result` (same `toolCallId`) is merged onto it so consumers see the
 * full call → result lifecycle.
 */
export interface AgentToolActivity {
  toolCallId?: string;
  toolName?: string;
  /** Call arguments — AI SDK v4 emits `input`, v5 emits `args`; either is kept. */
  input?: unknown;
  /** Result payload from the matching `tool-result` part. */
  output?: unknown;
  error?: string;
  code?: string;
  /** Step index the activity belongs to (null = received before any step-start). */
  step: number | null;
}

/**
 * Extract tool activity from a part defensively. Returns `undefined` for any
 * non tool-related part or when the shape is malformed, so callers can apply
 * it without disturbing the normal delta pipeline.
 */
export function toolActivityOf(part: AgentStreamPart): AgentToolActivity | undefined {
  if (!part || typeof part !== 'object') return undefined;
  const p = part as Record<string, unknown>;
  if (typeof p.type !== 'string') return undefined;
  if (p.type !== 'tool-call' && p.type !== 'tool-result') return undefined;

  const toolCallId = typeof p.toolCallId === 'string' ? p.toolCallId : undefined;
  const toolName = typeof p.toolName === 'string' ? p.toolName : undefined;
  const error = typeof p.error === 'string' ? p.error : undefined;
  const code = typeof p.code === 'string' ? p.code : undefined;

  if (p.type === 'tool-call') {
    const input = p.input !== undefined ? p.input : p.args;
    return { toolCallId, toolName, input, error, code, step: null };
  }
  return { toolCallId, toolName, output: p.output, error, code, step: null };
}

export interface ThinkingBufferState {
  step: number | null;
  maxSteps: number | null;
  text: string;
  reasoning: string;
  tokens: number;
  /** Captured tool-call/tool-result activity across all steps (additive). */
  toolParts?: AgentToolActivity[];
}

const INITIAL_STEP = -1;

/**
 * Accumulates stream parts. Text and reasoning are keyed by step index; a
 * step-start for a NEW step resets its bucket so replayed parts (sent again
 * when a client joins mid-run) never double up.
 */
export class ThinkingBuffer {
  private currentStep: number = INITIAL_STEP;
  private maxSteps: number | null = null;
  private textByStep = new Map<number, string>();
  private reasoningByStep = new Map<number, string>();
  private tokensByStep = new Map<number, number>();
  private toolPartsByStep = new Map<number, AgentToolActivity[]>();

  /** Feed one streamed part into the buffer. */
  addPart(part: AgentStreamPart): void {
    if (!part || typeof part.type !== 'string') return;

    switch (part.type) {
      case 'step-start': {
        // AI SDK v7 emits `stepNumber`; legacy envelopes use `step`. Accept
        // both so step-keyed idempotency holds against the real publisher.
        const p = part as Record<string, unknown>;
        const step =
          typeof part.step === 'number'
            ? part.step
            : typeof p.stepNumber === 'number'
              ? p.stepNumber
              : undefined;
        if (step !== undefined && step !== this.currentStep) {
          // New step: reset its bucket (idempotency against replay).
          this.textByStep.set(step, '');
          this.reasoningByStep.set(step, '');
          this.tokensByStep.set(step, 0);
          this.toolPartsByStep.set(step, []);
          // Deltas received before the first step-start belong to a stale
          // pre-step context — drop that debris when a real step begins.
          if (this.currentStep === INITIAL_STEP) {
            this.textByStep.delete(INITIAL_STEP);
            this.reasoningByStep.delete(INITIAL_STEP);
            this.tokensByStep.delete(INITIAL_STEP);
            this.toolPartsByStep.delete(INITIAL_STEP);
          }
          this.currentStep = step;
        }
        if (typeof part.maxSteps === 'number') this.maxSteps = part.maxSteps;
        break;
      }
      case 'text-delta': {
        const text = deltaTextOf(part);
        if (text === undefined) break;
        const step = this.currentStep;
        this.textByStep.set(step, (this.textByStep.get(step) ?? '') + text);
        break;
      }
      case 'reasoning-delta': {
        const text = deltaTextOf(part);
        if (text === undefined) break;
        const step = this.currentStep;
        this.reasoningByStep.set(step, (this.reasoningByStep.get(step) ?? '') + text);
        break;
      }
      case 'tool-call':
      case 'tool-result': {
        const activity = toolActivityOf(part);
        if (!activity) break;
        const step = this.currentStep;
        this.upsertToolActivity(step, { ...activity, step: step === INITIAL_STEP ? null : step });
        break;
      }
      case 'step-finish': {
        // AI SDK v7 reports tokens under `usage.totalTokens` on the part;
        // keep the legacy flat `tokens` field for older envelope shapes.
        const p = part as Record<string, unknown>;
        const usage = p.usage as
          | { totalTokens?: unknown; inputTokens?: unknown; outputTokens?: unknown }
          | undefined;
        const tokens =
          typeof part.tokens === 'number'
            ? part.tokens
            : typeof usage?.totalTokens === 'number'
              ? usage.totalTokens
              : undefined;
        if (typeof tokens === 'number' && this.currentStep !== INITIAL_STEP) {
          this.tokensByStep.set(this.currentStep, tokens);
        }
        break;
      }
      default:
        break;
    }
  }

  /**
   * Insert or merge tool activity for a step. Idempotent against replay: a
   * re-sent `tool-call` replaces the same `toolCallId` entry, and a matching
   * `tool-result` merges `output` onto the existing call entry instead of
   * appending a duplicate.
   */
  private upsertToolActivity(step: number, activity: AgentToolActivity): void {
    const list = this.toolPartsByStep.get(step) ?? [];
    const index = activity.toolCallId
      ? list.findIndex((t) => t.toolCallId === activity.toolCallId)
      : -1;
    if (index >= 0) {
      list[index] = { ...list[index], ...activity };
    } else {
      list.push(activity);
    }
    this.toolPartsByStep.set(step, list);
  }

  /** Full accumulated assistant text (all steps, applied order). */
  text(): string {
    return [...this.textByStep.entries()]
      .sort(([a], [b]) => a - b)
      .map(([, v]) => v)
      .join('');
  }

  /** Reasoning text for one step, or ALL steps merged when omitted. */
  reasoning(step?: number): string {
    if (step !== undefined) return this.reasoningByStep.get(step) ?? '';
    return [...this.reasoningByStep.entries()]
      .sort(([a], [b]) => a - b)
      .map(([, v]) => v)
      .join('');
  }

  /** Tool activity (calls + results) for one step, or ALL steps when omitted. */
  toolParts(step?: number): AgentToolActivity[] {
    if (step !== undefined) return this.toolPartsByStep.get(step) ?? [];
    return [...this.toolPartsByStep.entries()]
      .sort(([a], [b]) => a - b)
      .flatMap(([, v]) => v);
  }

  /** Estimated token total from step-finish `tokens` payloads. */
  tokens(): number {
    let total = 0;
    for (const t of this.tokensByStep.values()) total += t;
    return total;
  }

  get currentStepInfo(): { step: number | null; maxSteps: number | null } {
    return {
      step: this.currentStep === INITIAL_STEP ? null : this.currentStep,
      maxSteps: this.maxSteps,
    };
  }

  reset(): void {
    this.currentStep = INITIAL_STEP;
    this.maxSteps = null;
    this.textByStep.clear();
    this.reasoningByStep.clear();
    this.tokensByStep.clear();
    this.toolPartsByStep.clear();
  }

  state(): ThinkingBufferState {
    return {
      step: this.currentStepInfo.step,
      maxSteps: this.currentStepInfo.maxSteps,
      text: this.text(),
      reasoning: this.reasoning(),
      tokens: this.tokens(),
      toolParts: this.toolParts(),
    };
  }
}

export interface AgentStreamHandlers {
  /** Called for every normalized part (after the buffer-consumer parses it). */
  onPart?: (part: AgentStreamPart, at?: string) => void;
  /** Called when the room join succeeds / re-establishes after reconnect. */
  onSubscription?: () => void;
  onDisconnect?: (reason?: string) => void;
  onError?: (error: Error) => void;
}

export interface AgentStreamOptions {
  /** Optional replay source (e.g. GET .../stream-replay via RTK Query). */
  fetchReplay?: () => Promise<AgentStreamPart[]>;
}

/**
 * Join `agent:conversations:<conversationId>` and stream parts to the handler.
 * Re-subscribes automatically on socket.io reconnects (wsClient replays its
 * active topics). Returns an unsubscribe function.
 */
export function subscribeAgentStream(
  conversationId: string,
  handlers: AgentStreamHandlers,
  options?: AgentStreamOptions,
): () => void {
  const room = agentConversationRoom(conversationId);

  const subscribe = (): void => {
    wsClient.subscribe(room).catch(() => {
      handlers.onError?.(new Error(`Failed to subscribe to ${room}`));
    });
    if (options?.fetchReplay) {
      Promise.resolve()
        .then(() => options.fetchReplay?.())
        .then((parts) => {
          if (Array.isArray(parts)) {
            for (const part of parts) handlers.onPart?.(part, undefined);
          }
        })
        .catch((err: unknown) => {
          // Replay is best-effort — persistence is the source of truth.
          handlers.onError?.(err instanceof Error ? err : new Error(String(err)));
        });
    }
  };

  const unsubConnected = wsClient.on('connected', () => {
    subscribe();
    handlers.onSubscription?.();
  });
  const unsubDisconnected = wsClient.on('disconnected', (payload: unknown) => {
    const p = (payload ?? {}) as { reason?: string };
    handlers.onDisconnect?.(p.reason);
  });
  const unsubEvent = wsClient.on('agent:stream', (payload: unknown) => {
    const envelope = parseAgentStreamPayload(payload);
    if (envelope.part) {
      handlers.onPart?.(envelope.part, envelope.at);
    }
  });

  // Mirror the existing page connector pattern (MeetingsPage/PlanningPage).
  if (!wsClient.connected) {
    wsClient.connect().catch(() => {
      handlers.onError?.(new Error('WebSocket connection failed'));
    });
  } else {
    subscribe();
  }

  return () => {
    unsubConnected();
    unsubDisconnected();
    unsubEvent();
    wsClient.unsubscribe(room).catch(() => {});
  };
}